/**
 * TENDENCIA — ¿vamos a mejor o a peor?  (T047, FR-023b)
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ LA LÍNEA SE PARTE EN LOS HUECOS. NO SE CRUZA.                         ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Un día sin observar llega como `null`, y `connectNulls` se queda en `false`
 * (el defecto de recharts, pero se declara **explícito** justo porque cambiarlo
 * es una línea y el daño no se ve: la gráfica seguiría dibujándose preciosa,
 * simplemente mentiría). Dibujar una recta sobre un hueco es inventarse los
 * datos de en medio con aspecto de medición.
 *
 * Y tampoco se rellena con ceros: un 0 dice *"ese día no hubo ni una
 * conversación"* — que es una alarma, y falsa.
 *
 * ── POR QUÉ SON DOS GRÁFICAS Y NO UNA CON DOS EJES ──────────────────────────
 *
 * Volumen (conversaciones) y conversión (%) no comparten unidad. Meterlos en una
 * gráfica con dos escalas verticales permite colocar los ejes de forma que dos
 * series cuenten la historia que uno quiera — sube el eje derecho y la conversión
 * "supera" al volumen; bájalo y pasa lo contrario. Es el error de gráfica más
 * común que existe, y es indefendible: dos medidas, dos gráficas.
 *
 * ── COLOR ───────────────────────────────────────────────────────────────────
 *
 * Paleta categórica validada para fondo oscuro (checks de banda de luminosidad,
 * croma, separación bajo daltonismo y contraste): azul + verde, con ΔE 27,3 bajo
 * deuteranopía — muy por encima del mínimo de 8. No son colores elegidos a ojo.
 * Además cada serie va DIRECTAMENTE ETIQUETADA, así que la identidad nunca
 * depende solo del color.
 */
import { useQuery } from '@tanstack/react-query'
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import { api } from '../lib/api'

interface Contadores {
    total: number
    cerradasBien: number
    escaladas: number
    colgadas: number
}

interface DiaApi {
    fecha: string
    contadores: Contadores | null
    conversion: { numerador: number; denominador: number; tasa: number | null } | null
    minutosSinObservar: number
    parcial: boolean
}

interface TendenciaApi {
    dias: DiaApi[]
    huecos: { desde: string; hasta: string; minutos: number }[]
}

/** Paleta categórica, modo oscuro. Slots 1 y 2 — validados, no elegidos a ojo. */
const SERIE_1 = '#3987e5' // azul
const SERIE_2 = '#008300' // verde
const TINTA = '#a3a3a3'
const REJILLA = '#262626'

interface Punto {
    fecha: string
    etiqueta: string
    conversaciones: number | null
    enCita: number | null
    conversion: number | null
    parcial: boolean
    minutosSinObservar: number
}

function etiquetaDia(fecha: string): string {
    const [, m, d] = fecha.split('-')
    return `${d ?? ''}/${m ?? ''}`
}

/**
 * Agrupa los días sin datos en TRAMOS consecutivos.
 *
 * Necesario porque una banda de un solo día (`x1 === x2`) tiene anchura cero en
 * recharts y no se dibuja. Sin esto, "sombrear los huecos" no sombreaba nada — y
 * el hueco quedaba indistinguible de un margen vacío del gráfico.
 */
export function tramosSinDatos(puntos: { etiqueta: string; conversaciones: number | null }[]): {
    desde: string
    hasta: string
}[] {
    const tramos: { desde: string; hasta: string }[] = []
    for (const p of puntos) {
        if (p.conversaciones !== null) continue
        const ultimo = tramos.at(-1)
        const anterior = puntos[puntos.indexOf(p) - 1]
        if (ultimo && anterior && anterior.conversaciones === null) ultimo.hasta = p.etiqueta
        else tramos.push({ desde: p.etiqueta, hasta: p.etiqueta })
    }
    return tramos
}

function aPuntos(dias: DiaApi[]): Punto[] {
    return dias.map((d) => ({
        fecha: d.fecha,
        etiqueta: etiquetaDia(d.fecha),
        // `null` viaja hasta recharts TAL CUAL. Es lo que parte la línea.
        conversaciones: d.contadores?.total ?? null,
        enCita: d.contadores?.cerradasBien ?? null,
        conversion: d.conversion?.tasa === null || d.conversion === null ? null : Math.round(d.conversion.tasa * 100),
        parcial: d.parcial,
        minutosSinObservar: d.minutosSinObservar,
    }))
}

interface PropsTooltip {
    active?: boolean
    payload?: { name?: string; value?: number | null; color?: string; payload?: Punto }[]
    label?: string
}

function Globo({ active, payload, label }: PropsTooltip) {
    if (!active || !payload?.length) return null
    const p = payload[0]?.payload

    return (
        <div className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs shadow-lg">
            <p className="mb-1 font-medium text-neutral-200">{label}</p>
            {payload.map((s) => (
                <p key={s.name} className="flex items-center gap-2">
                    <span className="inline-block size-2 rounded-full" style={{ background: s.color }} />
                    {/* El texto va en tinta neutra; el color lo lleva el punto. */}
                    <span className="text-neutral-400">{s.name}</span>
                    <span className="ml-auto tabular-nums text-neutral-200">{s.value ?? '—'}</span>
                </p>
            ))}
            {p?.parcial && (
                // El aviso viaja CON el dato: quien mira ese punto tiene que saber
                // que le faltan minutos, no enterarse por una nota al pie.
                <p className="mt-1 border-t border-neutral-800 pt-1 text-amber-500">
                    ⚠️ faltan {p.minutosSinObservar} min sin observar
                </p>
            )}
        </div>
    )
}

export function Tendencia({ dias, incluirTests = false }: { dias: number; incluirTests?: boolean }) {
    const { data, isPending, isError } = useQuery<TendenciaApi>({
        // La bandera entra en la clave: si no, TanStack serviría de caché la
        // serie del otro estado y la gráfica contradiría a los contadores.
        queryKey: ['tendencia', dias, incluirTests],
        queryFn: () =>
            api.get<TendenciaApi>(`/overview/trends?dias=${String(dias)}&incluirTests=${incluirTests ? '1' : '0'}`),
    })

    if (isPending) return <p className="text-xs text-neutral-600">Cargando la tendencia…</p>
    if (isError || !data) return <p className="text-xs text-rose-400">No se pudo cargar la tendencia.</p>

    const puntos = aPuntos(data.dias)
    const sinDatos = puntos.filter((p) => p.conversaciones === null)
    const parciales = puntos.filter((p) => p.parcial)

    return (
        <section className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Tendencia</h3>

            {/* ── VOLUMEN ── dos series, misma unidad, un solo eje ───────────── */}
            <div>
                <div className="mb-1 flex flex-wrap items-center gap-4 text-[11px]">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIE_1 }} />
                        <span className="text-neutral-400">Conversaciones</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIE_2 }} />
                        <span className="text-neutral-400">Acabaron en cita</span>
                    </span>
                </div>
                <div className="h-[160px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={puntos} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke={REJILLA} vertical={false} />
                        <XAxis dataKey="etiqueta" tick={{ fill: TINTA, fontSize: 10 }} tickLine={false} axisLine={{ stroke: REJILLA }} />
                        {/* `width` holgado: con el eje pegado al borde, "1800" se recortaba a
                            "800" — un orden de magnitud de diferencia, en silencio. */}
                        <YAxis tick={{ fill: TINTA, fontSize: 10 }} tickLine={false} axisLine={false} width={52} />
                        <Tooltip content={<Globo />} cursor={{ stroke: TINTA, strokeWidth: 1 }} />

                        {/*
                          LOS HUECOS, SOMBREADOS — para que el hueco se VEA como
                          hueco y no como un margen del gráfico.

                          Un `x1===x2` (un día suelto) tiene ANCHURA CERO y no
                          pinta nada: la primera versión "sombreaba" siete días
                          y no se veía absolutamente nada. Se agrupan los días
                          consecutivos y se pinta UNA banda por tramo.
                        */}
                        {tramosSinDatos(puntos).map((t) => (
                            <ReferenceArea
                                key={t.desde}
                                x1={t.desde}
                                x2={t.hasta}
                                fill="#f59e0b"
                                fillOpacity={0.07}
                                ifOverflow="extendDomain"
                            />
                        ))}

                        <Line
                            type="monotone"
                            dataKey="conversaciones"
                            name="Conversaciones"
                            stroke={SERIE_1}
                            strokeWidth={2}
                            dot={{ r: 3, fill: SERIE_1, strokeWidth: 0 }}
                            // ⛔ NUNCA `true`. Ver la cabecera: cruzar el hueco es inventar.
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="enCita"
                            name="Acabaron en cita"
                            stroke={SERIE_2}
                            strokeWidth={2}
                            dot={{ r: 3, fill: SERIE_2, strokeWidth: 0 }}
                            connectNulls={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
                </div>
            </div>

            {/* ── CONVERSIÓN ── otra unidad → OTRA gráfica, no un segundo eje ── */}
            <div>
                <p className="mb-1 text-[11px] text-neutral-400">Conversión (% de las que terminaron)</p>
                <div className="h-[120px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={puntos} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke={REJILLA} vertical={false} />
                        <XAxis dataKey="etiqueta" tick={{ fill: TINTA, fontSize: 10 }} tickLine={false} axisLine={{ stroke: REJILLA }} />
                        <YAxis
                            domain={[0, 100]}
                            // Marcas explícitas: recharts elegía 0/25/50/100,
                            // desiguales, que deforman la lectura de la pendiente.
                            ticks={[0, 25, 50, 75, 100]}
                            // `interval={0}`: sin él recharts descarta etiquetas
                            // que cree que chocan, y la escala queda 0/25/50/100
                            // — desigual, y una escala desigual deforma la
                            // pendiente que el ojo lee.
                            interval={0}
                            tick={{ fill: TINTA, fontSize: 10 }}
                            tickLine={false}
                            axisLine={false}
                            width={52}
                            unit="%"
                        />
                        <Tooltip content={<Globo />} cursor={{ stroke: TINTA, strokeWidth: 1 }} />

                        {/* El mismo sombreado que arriba: si el hueco solo se
                            señalara en una de las dos, la otra mentiría. */}
                        {tramosSinDatos(puntos).map((t) => (
                            <ReferenceArea
                                key={t.desde}
                                x1={t.desde}
                                x2={t.hasta}
                                fill="#f59e0b"
                                fillOpacity={0.07}
                                ifOverflow="extendDomain"
                            />
                        ))}

                        <Line
                            type="monotone"
                            dataKey="conversion"
                            name="Conversión"
                            stroke={SERIE_1}
                            strokeWidth={2}
                            dot={{ r: 3, fill: SERIE_1, strokeWidth: 0 }}
                            connectNulls={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
                </div>
            </div>

            {/* La letra pequeña, dicha — no escondida en un tooltip que hay que buscar. */}
            {(sinDatos.length > 0 || parciales.length > 0) && (
                <p className="border-t border-neutral-900 pt-2 text-[11px] text-amber-600/90">
                    ⚠️{' '}
                    {sinDatos.length > 0 &&
                        `${sinDatos.length} ${sinDatos.length === 1 ? 'día sin observar' : 'días sin observar'} (la línea se corta ahí, no se cruza)`}
                    {sinDatos.length > 0 && parciales.length > 0 && ' · '}
                    {parciales.length > 0 &&
                        `${parciales.length} ${parciales.length === 1 ? 'día observado a medias' : 'días observados a medias'}`}
                    .
                </p>
            )}
        </section>
    )
}
